import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ChevronDown, ChevronRight, Database, Search } from 'lucide-react';
import type { DetrixEvent, DetrixSession } from '../../types';
import { usePlaybackStore } from '../../store/usePlaybackStore';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatSessionOffset } from '../../utils/time';
import { formatUnknownJsonForDisplay } from '../../utils/prettifyJson';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function isEventError(ev: DetrixEvent): boolean {
  return ev.type === 'error' || ev.type === 'system.error';
}

interface EventRowProps {
  event: DetrixEvent;
  isSelected: boolean;
  isAtPlayhead?: boolean;
  onSelect: () => void;
}

const EventRow: React.FC<EventRowProps> = ({ event, isSelected, isAtPlayhead, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const err = isEventError(event);
  const errorSelected = isSelected && err;

  return (
    <div
      data-event-id={event.id}
      className={cn(
        'border-b border-[var(--border-subtle)] transition-colors',
        isSelected && !errorSelected ? 'bg-[var(--accent)]/10' : '',
        errorSelected ? 'bg-[var(--list-row-error-bg)] ring-1 ring-inset ring-[var(--list-row-error-ring)]' : '',
        !isSelected ? 'hover:bg-[var(--bg-elevated)]' : '',
        isAtPlayhead && !isSelected ? 'bg-[var(--accent)]/8 ring-1 ring-inset ring-[var(--accent)]/25' : '',
      )}
    >
      <div
        role="button"
        tabIndex={0}
        className="flex cursor-pointer items-center gap-2 px-4 py-2 text-[12px]"
        onClick={() => {
          onSelect();
          setIsOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
            setIsOpen((o) => !o);
          }
        }}
      >
        {isOpen ? (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-muted)]" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-muted)]" />
        )}
        <span className="w-16 tabular-nums text-[11px] text-[var(--text-muted)]">
          {formatSessionOffset(event.timestamp)}
        </span>
        <span className={cn('font-bold', err ? 'text-[var(--error)]' : 'text-[var(--accent)]')}>{event.type}</span>
        <span className="mono-small truncate text-[var(--text-muted)]">{event.selector ?? ''}</span>
      </div>

      {isOpen ? (
        <div className="bg-[var(--bg-editor)] p-4">
          <pre className="mono-small whitespace-pre-wrap break-words text-[var(--code-json-fg)]">
            {formatUnknownJsonForDisplay(event.payload)}
          </pre>
        </div>
      ) : null}
    </div>
  );
};

export const EventsPanel: React.FC<{ session: DetrixSession }> = ({ session }) => {
  const { events = [] } = session;
  const { focusedTimestamp, isPlaying } = usePlaybackStore();
  const { selectedId, setSelectedId } = useSessionUiStore();
  const [filter, setFilter] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => e.type.toLowerCase().includes(filter.toLowerCase()));
  }, [events, filter]);

  const playheadEventId = useMemo(() => {
    if (focusedTimestamp == null || filteredEvents.length === 0) return null;
    let id: string | null = null;
    for (const e of filteredEvents) {
      if (e.timestamp <= focusedTimestamp) id = e.id;
      else break;
    }
    return id;
  }, [filteredEvents, focusedTimestamp]);

  useLayoutEffect(() => {
    if (!playheadEventId || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-event-id="${playheadEventId}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: isPlaying ? 'auto' : 'smooth' });
  }, [focusedTimestamp, isPlaying, playheadEventId]);

  useLayoutEffect(() => {
    if (!isPlaying || !playheadEventId || playheadEventId === selectedId) return;
    setSelectedId(playheadEventId);
  }, [isPlaying, playheadEventId, selectedId, setSelectedId]);

  useLayoutEffect(() => {
    if (!selectedId || !listRef.current) return;
    const ev = filteredEvents.find((e) => e.id === selectedId);
    if (!ev || !isEventError(ev)) return;
    listRef.current.querySelector(`[data-event-id="${selectedId}"]`)?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedId, filteredEvents]);

  if (events.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center text-[var(--text-muted)]">
        <Database className="mb-2 h-8 w-8 opacity-20" />
        <p>No raw events captured.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b bg-[var(--bg-sidebar)] p-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Filter by event type..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full rounded border bg-[var(--bg-editor)] py-1.5 pl-8 pr-3 text-[12px] outline-none focus:border-[var(--accent)]"
          />
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-auto">
        {filteredEvents.map((event) => (
          <EventRow
            key={event.id}
            event={event}
            isSelected={selectedId === event.id}
            isAtPlayhead={playheadEventId === event.id}
            onSelect={() => setSelectedId(event.id)}
          />
        ))}
      </div>
    </div>
  );
};
